import { useState, useEffect } from 'react';

/**
 * Represents the calculated dimensions for a responsive grid layout
 */
export interface GridDimensions {
  /** Number of rows in the grid */
  rows: number;
  /** Number of columns in the grid */
  columns: number;
  /** Total number of cards that fit in the grid (rows * columns) */
  totalCards: number;
}

/**
 * Return type for the useResponsiveGrid hook
 */
interface ResponsiveGridHook {
  /** Grid dimensions for preparing orders */
  preparing: GridDimensions;
  /** Grid dimensions for ready orders */
  ready: GridDimensions;
}

/**
 * Hook that calculates optimal grid dimensions based on viewport size.
 * Automatically recalculates when the window is resized.
 *
 * The grid is designed to show order cards in a two-column layout (preparing/ready),
 * with each column taking half the screen width.
 *
 * @returns Object containing grid dimensions for both preparing and ready columns
 */
export const useResponsiveGrid = (): ResponsiveGridHook => {
  // Initialize with default dimensions
  const [dimensions, setDimensions] = useState<ResponsiveGridHook>({
    preparing: { rows: 4, columns: 3, totalCards: 12 },
    ready: { rows: 4, columns: 3, totalCards: 12 },
  });

  /**
   * Calculates the optimal grid dimensions based on available space
   *
   * @param width - Total viewport width in pixels
   * @param height - Total viewport height in pixels
   * @returns Calculated grid dimensions
   */
  const calculateGridDimensions = (
    width: number,
    height: number,
  ): GridDimensions => {
    // Card and layout constants
    const baseCardWidth = 200;
    const baseCardHeight = 120;
    const gap = 16; // Gap between cards
    const headerHeight = 60; // Reserved space for header
    const footerHeight = 60; // Reserved space for footer

    // Calculate available space for one column (half width minus gaps)
    const availableWidth = width / 2 - gap * 4;
    const availableHeight = height - headerHeight - footerHeight - gap * 2;

    // Calculate maximum possible grid size based on card dimensions
    const maxColumns = Math.floor(availableWidth / (baseCardWidth + gap));
    const maxRows = Math.floor(availableHeight / (baseCardHeight + gap));

    // Apply constraints: minimum 3x4 grid, maximum 5x6 grid
    const columns = Math.max(3, Math.min(maxColumns, 5));
    const rows = Math.max(4, Math.min(maxRows, 6));

    return {
      rows,
      columns,
      totalCards: rows * columns,
    };
  };

  useEffect(() => {
    /**
     * Updates grid dimensions based on current window size
     */
    const updateDimensions = (): void => {
      const width = window.innerWidth;
      const height = window.innerHeight;

      const gridDimensions = calculateGridDimensions(width, height);

      // Currently both preparing and ready use the same dimensions
      setDimensions({
        preparing: gridDimensions,
        ready: gridDimensions,
      });
    };

    // Calculate initial dimensions
    updateDimensions();

    // Listen for window resize events
    window.addEventListener('resize', updateDimensions);

    // Cleanup event listener on unmount
    return () => window.removeEventListener('resize', updateDimensions);
  }, []);

  return dimensions;
};
